/* eslint-disable no-underscore-dangle */
/* eslint-disable import/no-extraneous-dependencies */
/**
 * @name Hotel Room Booking System
 * @description Hotel Room Booking and Management System Software ~ Developed By Mr. Josia Yvan
 * @copyright ©2025 ― Mr. Josia Yvan. All rights reserved.
 * @version v0.0.1
 */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import 'moment/locale/fr'; // Import the desired locale, e.g., French

import {
  Button, DatePicker, Form, Input, Modal, Result, Select
} from 'antd';

import useFetchData from '../../hooks/useFetchData';
import { reFetchData } from '../../store/slice/appSlice';
import ApiService from '../../utils/apiService';
import { getSessionUser, setSessionUserKeyAgainstValue } from '../../utils/authentication';
import notificationWithIcon from '../../utils/notification';
import PageLoader from './PageLoader';

// Set the locale globally
moment.locale('fr'); // or whichever locale you need

function ProfileEditModal({ editProfileModal, setEditProfileModal, user }) {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [fetchLoading, fetchError, fetchResponse] = useFetchData(`/user/${user._id}`);

  useEffect(() => {
    console.log('fetchError::: ', fetchError);
  }, [fetchResponse, fetchError]);

  useEffect(() => {
    if (fetchResponse) {
      form.setFieldsValue({
        nfcCard: fetchResponse?.data?.nfcCard,
        firstname: fetchResponse?.data?.firstname,
        quartier: fetchResponse?.data?.quartier,
        role: fetchResponse?.data?.role,
        cardType: fetchResponse?.data?.cardType,
        civility: fetchResponse?.data?.civility,
        phone: fetchResponse?.data?.phone,
        birthdate: fetchResponse?.data?.birthdate ? moment(fetchResponse.data.birthdate) : undefined,
        size: fetchResponse?.data?.size,
        branch: fetchResponse?.data?.branch,
        referent: fetchResponse?.data?.referent ? fetchResponse?.data?.referent?.matricule : undefined
      });
    }
  }, [fetchResponse, form]);

  const onFinish = (values) => {
    setLoading(true);

    ApiService.put(`/user/${user._id}`, values)
      .then((response) => {
        setLoading(false);

        if (response?.result_code === 0) {
          notificationWithIcon(
            'success',
            'SUCCESS',
            response?.result?.message || 'Profile updated successfully'
          );

          // Update session user if necessary
          if (user._id === getSessionUser().id) {
            Object.keys(values).forEach((key) => {
              setSessionUserKeyAgainstValue(key, values[key]);
            });
          }

          form.resetFields();
          dispatch(reFetchData());
          setEditProfileModal(false);
        } else {
          notificationWithIcon(
            'error',
            'ERROR',
            response?.result?.error?.message || 'Something went wrong. App server error'
          );
        }
      })
      .catch((err) => {
        setLoading(false);

        // Extraire un message d’erreur compréhensible
        let errorMessage = 'Sorry! Something went wrong.';
        if (err?.response?.data?.result?.error) {
          const apiError = err.response.data.result.error;
          errorMessage =
          typeof apiError === 'string' ?
            apiError :
            apiError.message || JSON.stringify(apiError);
        }

        notificationWithIcon('error', 'ERROR', errorMessage);
      });
  };

  return (
    <Modal
      title='Edit Profile Information'
      visible={editProfileModal}
      onOk={() => setEditProfileModal(false)}
      onCancel={() => setEditProfileModal(false)}
      footer={null}
      width={800}
    >
      {fetchLoading ? (<PageLoader />) : fetchError ? (
        <Result title='Failed to fetch' subTitle={fetchError} status='error' />
      ) : (
        <Form
          form={form}
          name='edit-profile-form'
          onFinish={onFinish}
          layout='vertical'
        >
          <Form.Item label='Matricule' name='nfcCard'>
            <Input placeholder='Matricule' size='large' allowClear readOnly />
          </Form.Item>

          <Form.Item label='Prénom' name='firstname' rules={[{ required: true, message: 'Please input your firstname!' }]}>
            <Input placeholder='Prénom' size='large' allowClear />
          </Form.Item>

          <Form.Item label='Quartier' name='quartier'>
            <Input placeholder='Quartier' size='large' allowClear />
          </Form.Item>

          <Form.Item label='Role' name='role' rules={[{ required: true, message: 'Please select a role!' }]}>
            <Select placeholder='Select Role' size='large' allowClear>
              <Select.Option value='Mpiandraikitra kely'>Mpiandraikitra kely</Select.Option>
              <Select.Option value='Mpiandraikitra be'>Mpiandraikitra be</Select.Option>
              <Select.Option value='Mpiandry'>Mpiandry</Select.Option>
              <Select.Option value='Solom-pitandrina'>Solom-pitandrina</Select.Option>
              <Select.Option value='Reverend'>Reverend</Select.Option>
              <Select.Option value='Pasteur'>Pasteur</Select.Option>
              <Select.Option value='Pasteur Stagiaire'>Pasteur Stagiaire</Select.Option>
              <Select.Option value='Mpino tsotra'>Mpino tsotra</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label='Type de carte' name='cardType'>
            <Select placeholder='Select Card Type' size='large' allowClear>
              <Select.Option value='Tsotra'>Tsotra</Select.Option>
              <Select.Option value='Premium'>Premium</Select.Option>
              <Select.Option value='VIP'>VIP</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label='Civilité' name='civility'>
            <Select placeholder='Select Civility' size='large' allowClear>
              <Select.Option value='Mr'>Mr</Select.Option>
              <Select.Option value='Me'>Me</Select.Option>
              <Select.Option value='Mlle'>Mlle</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label='Téléphone' name='phone'>
            <Input placeholder='Téléphone' size='large' allowClear />
          </Form.Item>

          <Form.Item label='Date de naissance' name='birthdate'>
            <DatePicker className='w-full' placeholder='Select Date' format='YYYY-MM-DD' size='large' allowClear />
          </Form.Item>

          <Form.Item label='Taille' name='size'>
            <Select placeholder='Select Size' size='large' allowClear>
              <Select.Option value='S'>S</Select.Option>
              <Select.Option value='M'>M</Select.Option>
              <Select.Option value='L'>L</Select.Option>
              <Select.Option value='XL'>XL</Select.Option>
              <Select.Option value='XXL'>XXL</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label='Branch' name='branch'>
            <Input placeholder='Branch' size='large' allowClear />
          </Form.Item>

          <Form.Item label='Référent' name='referent'>
            <Input placeholder='Référent' size='large' allowClear />
          </Form.Item>

          <Form.Item>
            <Button type='primary' htmlType='submit' size='large' loading={loading} disabled={loading}>
              Update
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}

export default React.memo(ProfileEditModal);
